import React, { useEffect, useState } from 'react';
import { Target, Trophy } from 'lucide-react';
import { salesApi } from '../salesApi';
import { useT, fmtMoney, fmtDate } from '../salesI18n';

const Bar = ({ pct, reached }) => (
  <div className="h-3 rounded-full bg-slate-200/70 overflow-hidden">
    <div className={`h-full rounded-full transition-[width] duration-700 ${reached ? 'bg-teal-600' : pct >= 60 ? 'bg-teal-500' : pct >= 30 ? 'bg-amber-400' : 'bg-rose-400'}`} style={{ width: `${Math.max(2, Math.min(100, pct))}%` }} />
  </div>
);

export const GoalCard = ({ isManager = false }) => {
  const { t, lang } = useT();
  const [data, setData] = useState(null);

  useEffect(() => { salesApi.get('/goal').then((r) => setData(r.data)).catch(() => setData({ error: true })); }, []);
  if (!data || data.error) return null;

  const raw = fmtDate(`${data.month}-15`, lang, { month: 'long' });
  const monthName = raw.charAt(0).toUpperCase() + raw.slice(1);
  const rows = isManager ? data.team : [data];
  if (!isManager && !(data.goal > 0)) {
    return (
      <section className="bg-card rounded-xl border border-border p-4 sm:p-5 shadow-sm flex items-center gap-3" data-testid="goal-card">
        <span className="h-10 w-10 rounded-2xl bg-slate-100 text-slate-500 flex items-center justify-center shrink-0"><Target className="h-5 w-5" /></span>
        <div><p className="font-semibold text-sm text-slate-900">{t.monthGoal(monthName)}</p><p className="text-xs text-slate-500" data-testid="goal-none">{t.noGoal}</p></div>
      </section>
    );
  }

  return (
    <section className="bg-card rounded-xl border border-border p-4 sm:p-6 shadow-sm" data-testid="goal-card">
      <div className="flex items-center gap-3 mb-4">
        <span className={`h-10 w-10 rounded-2xl flex items-center justify-center shrink-0 shadow-sm ${data.reached && !isManager ? 'bg-teal-600 text-white' : 'bg-amber-400 text-white'}`}>{data.reached && !isManager ? <Trophy className="h-5 w-5" /> : <Target className="h-5 w-5" />}</span>
        <div className="min-w-0">
          <h2 className="font-display font-bold text-base sm:text-lg text-slate-900">{isManager ? t.teamGoal(monthName) : t.monthGoal(monthName)}</h2>
          {!isManager && <p className="text-xs text-slate-500" data-testid="goal-summary">{data.reached ? t.goalReached : t.goalRemaining(fmtMoney(data.remaining, lang))}</p>}
        </div>
        {!isManager && <span className={`ml-auto text-2xl font-extrabold tabular-nums ${data.reached ? 'text-teal-700' : 'text-slate-900'}`} data-testid="goal-percent">{data.percent}%</span>}
      </div>
      <div className="space-y-3">
        {rows.map((g) => (
          <div key={g.seller} data-testid={`goal-row-${g.seller}`}>
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="font-semibold text-slate-700">{isManager ? (g.seller_name || g.seller) : `${g.sales_count} ${t.closedSales}`}</span>
              <span className="tabular-nums text-slate-600"><b className="text-slate-900">{fmtMoney(g.achieved, lang)}</b> / {g.goal > 0 ? fmtMoney(g.goal, lang) : '—'}{isManager && g.goal > 0 ? ` · ${g.percent}%` : ''}</span>
            </div>
            <Bar pct={g.goal > 0 ? g.percent : 0} reached={g.reached} />
          </div>
        ))}
        {isManager && rows.length === 0 && <p className="text-xs text-slate-500">{t.noGoal}</p>}
      </div>
    </section>
  );
};
